import React from 'react';
import {
  sourceConnectionConfig,
  sourceLabel,
  type ConnectableSource,
} from '../../domain/sourceConnection';
import { useSourceConnectionGate, type SourceGateInput } from '../../hooks/useSourceConnectionGate';
import { CardSkeleton, ErrorState } from '../../ui/common';
import { openSettingsSection } from '../settings/settingsDeepLink';
import { SourceConnectionPanel } from './SourceConnectionPanel';

export interface SourceGateProps extends SourceGateInput {
  source: ConnectableSource;
  isRetrying: boolean;
  onRetry: () => void;
  /** Normal feed UI (filters, toolbar, cards). Rendered only when connected. */
  children: React.ReactNode;
}

/**
 * Wraps a provider feed: while the connection is unknown it shows a skeleton,
 * when the provider is not connected it shows the shared connection panel
 * (and nothing else — no filters), when the probe fails it shows the probe
 * error, otherwise it renders the feed as-is.
 */
export const SourceGate: React.FC<SourceGateProps> = ({
  source,
  isRetrying,
  onRetry,
  children,
  ...input
}) => {
  const gate = useSourceConnectionGate(source, input);
  if (gate.state === 'checking') {
    return (
      <CardSkeleton
        count={3}
        variant={source}
        height={sourceConnectionConfig(source).skeletonHeight}
      />
    );
  }
  if (gate.state === 'not_configured') {
    return <SourceConnectionPanel source={source} onRetry={onRetry} isRetrying={isRetrying} />;
  }
  if (gate.state === 'credentials_rejected') {
    return (
      <SourceConnectionPanel
        source={source}
        variant="rejected"
        detail={gate.probeMessage}
        onRetry={onRetry}
        isRetrying={isRetrying}
      />
    );
  }
  if (gate.state === 'probe_error') {
    return (
      <ErrorState
        title={`Error en ${sourceLabel(source)}`}
        message={gate.probeMessage ?? `${sourceLabel(source)} rechazó las credenciales guardadas.`}
        onRetry={onRetry}
        isRetrying={isRetrying}
        actionLabel={`Abrir Configuración → ${sourceLabel(source)}`}
        onAction={() => openSettingsSection(sourceConnectionConfig(source).settingsSection)}
      />
    );
  }
  return <>{children}</>;
};
